import { FC, ReactNode, useEffect, useState } from "react";
import { Button, Card, Form, Input, message } from "../../design-system";
import { useUserState } from "../../common/UserContext";
import { updateUser } from "../../common/api/UserClient";
import { handleError } from "../../common/ErrorHandling";
import { User } from "../../types/UserGroup";
import classes from "./ProfileCard.module.less";

export interface ProfileCardProps {
    user: User;
    onSaved: (user: User) => void;
    /** Something to show in the card's top right corner (an admin's status action). */
    extra?: ReactNode;
}

const fullName = (user: User) => `${user.firstName} ${user.lastName}`.trim();

/** The first word becomes the first name, the rest the last name; the user never sees the split. */
export function splitName(name: string): { firstName: string; lastName: string } {
    const [firstName = "", ...rest] = name.trim().split(/\s+/);
    return { firstName, lastName: rest.join(" ") };
}

/** A user's name, editable as one field, with their email, which is not. */
export const ProfileCard: FC<ProfileCardProps> = ({ user, onSaved, extra }) => {
    const { me, refreshMe } = useUserState();
    const [draft, setDraft] = useState(fullName(user));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(fullName(user));
    }, [user]);

    const changed = draft.trim() !== fullName(user);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateUser({ ...user, ...splitName(draft) });
            onSaved(updated);
            if (updated.id === me.id) refreshMe();
            void message.success("Profile saved");
        } catch (error: any) {
            handleError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card title="Profile" extra={extra} className={classes.card}>
            <Form layout="vertical" className={classes.form}>
                <Form.Item label="Name">
                    <Input value={draft} onChange={(e) => setDraft(e.target.value)} />
                </Form.Item>
                <Form.Item label="Email">
                    <Input value={user.email} disabled />
                </Form.Item>
                <div className={classes.actions}>
                    <Button disabled={!changed || saving} onClick={() => setDraft(fullName(user))}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={!changed || !draft.trim()}
                        loading={saving}
                        onClick={save}
                    >
                        Save
                    </Button>
                </div>
            </Form>
        </Card>
    );
};
